import { Row, Col, Button, Card, CardBody, Modal, ModalBody } from 'reactstrap';
import { useContext, useEffect, useState, useMemo } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useReactTable } from '@tanstack/react-table';
import { renderIcon } from '../../shared/components/icons';
import moment from 'moment';

import { CampaignContext } from '../../context/campaign';
import { AdminContext } from '../../context/admin';
import {
    fetchMyCampaign,
    deleteTeamMember,
    postCampaignContact,
} from '../../services/campaign';
import {
    ITeamMembers,
    ITeamMemberItem,
    MyCampaignResponse,
} from '../../types/campaign';
import TableContainer from '../Campaign/components/TableContainer';
import { CampaignHeaderSection } from '../../shared/components/cards/CampaignItemsCard';
import { Loading } from '~/shared/components/Loading';
import Tooltip from './Tooltip';
import { AuthContext } from '~/context/auth';
import { getGroupLabel } from '~/shared/utils';
import { ContactModal } from './ContactModal';

export const Team = ({ }: any) => {
    const [{ user, person }, userDispatch] = useContext(AuthContext);
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);
    const [isLimited, setIsLimited] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const [teamMembers, setTeamMembers] = useState<ITeamMembers>();
    const [selfPrimary, setSelfPrimary] = useState<boolean>(false);
    const [primaryContact, setPrimaryContact] = useState<ITeamMemberItem>();
    const [contactModal, setContactModal] = useState<string | null>(null);
    const [selfEmergency, setSelfEmergency] = useState<boolean>(false);
    const [emergencyContact, setEmergencyContact] = useState<ITeamMemberItem>();
    const [permissions_legend, setPermissionsLegend] = useState<[]>();
    const [deleteModal, setDeleteModal] = useState<ITeamMemberItem | null>();
    const [deleting, setDeleting] = useState(false);
    const history = useHistory();

    const formatTeamMember = (member: ITeamMemberItem) => {
        if (!member) {
            return;
        }
        const { person } = member;
        const middleName = person.middle_name ? ` ${person.middle_name}` : '';
        const name = `${person.first_name}${middleName} ${person.last_name}`;
        return {
            id: member.id,
            name,
            email: person.email,
            phone: person.phone,
            relationship: member.relationship_to_patient?.name,
            access: member.access,
            permissions: member.permissions,
            update_date: moment(member.update_date).format('MM/D/Y hh:mm A'),
        };
    };

    const data = useMemo(() => {
        if (teamMembers) {
            return teamMembers.map((member) => {
                return formatTeamMember(member);
            });
        } else {
            return [];
        }
    }, [teamMembers]);

    const get_permission_icons = (perms) => {
        return perms.map((item, i) => {
            return (
                <span key={i} className='me-2'>
                    {renderIcon(item)}
                </span>
            );
        });
    };

    // function ToolTipItem(id) {
    //   const [tooltipOpen, setTooltipOpen] = useState(false);
    //   const toggle = () => setTooltipOpen(!tooltipOpen);

    //   return (
    //     <Tooltip
    //       placement='top'
    //       isOpen={tooltipOpen}
    //       target={`tooltip-${id}`}
    //       toggle={toggle}
    //     >
    //       The Last Activity column automatically logs the last time this user
    //       accessed this campaign or made changes. We only use this information to
    //       improve our technical support for this campaign.
    //     </Tooltip>
    //   );
    // }

    const columns = useMemo(
        () => [
            {
                id: 'name',
                header: 'Name',
                accessorKey: 'name', // accessor is the "key" in the data
            },
            {
                id: 'relationship',
                header: 'Relationship',
                accessorKey: 'relationship',
            },
            {
                id: 'role',
                header: 'Role',
                accessorKey: 'access',
                accessorFn: (props) => {
                    return getGroupLabel(props.access);
                },
            },
            {
                id: 'permissions',
                header: 'Permissions',
                accessorKey: 'permissions',
                accessorFn: (props) => {
                    if (
                        !props.permissions.includes('emergency_contact') &&
                        emergencyContact?.person
                    ) {
                        const middleName = emergencyContact.person.middle_name 
                            ? ` ${emergencyContact.person.middle_name}` 
                            : '';
                        const emergencyFullName = `${emergencyContact.person.first_name}${middleName} ${emergencyContact.person.last_name}`;
                        if (props.name === emergencyFullName) {
                            props.permissions.push('emergency_contact');
                        }
                    }

                    return get_permission_icons(props.permissions);
                },
            },
            {
                id: 'last_activity',
                header: 'Last Activity',
                accessorKey: 'update_date',
                accessorFn: (props) => {
                    return (
                        <Tooltip
                            text='Last time the user accessed this campaign. For technical support purposes only.'
                            position='top'
                        >
                            {props.update_date}
                        </Tooltip>
                    );
                },
            },
            {
                id: 'edit_delete',
                header: 'Edit/Delete',
                accessorKey: '',
                accessorFn: (props) => {
                    const member = teamMembers?.find((x) => x.id == props.id);
                    if (!member || isLimited) {
                        return <div />;
                    }
                    return (
                        <span>
                            <Link
                                to={{
                                    pathname: `/team/edit/${member.id}`,
                                    state: { member },
                                }}
                                className='btn btn-link p-1 m-0'
                                style={{ fontSize: '20px' }}
                            >
                                {renderIcon('edit')}
                            </Link>
                            <Button
                                onClick={() => setDeleteModal(member)}
                                color='link'
                                className='p-1 m-0 text-danger'
                                style={{ fontSize: '20px' }}
                            >
                                {renderIcon('delete')}
                            </Button>
                        </span>
                    );
                },
            },
        ],
        [teamMembers]
    );

    function AddTeamMemberCard() {
        return (
            <div>
                <h1 className='h2'>Team</h1>
                <p>
                    Invite friends to help you manage your Campaign Page, submit
                    fund requests, and more.
                </p>

                {!isLimited && (
                    <Button
                        color='primary'
                        size='sm'
                        onClick={() => history.push('/team/add')}
                    >
                        {renderIcon('add')} Add Team Member
                    </Button>
                )}
            </div>
        );
    }

    useEffect(() => {
        if (campaign) {
            const { campaignDetail, campaignApplication } = campaign;
            if (!campaignDetail || !campaignApplication) {
                history.push('/dashboard');
            }

            if (campaignApplication?.pc_type === 1) {
                setSelfPrimary(true);
            }

            const emergency = campaignDetail.team_members.filter(
                (x) => x.is_emergency_contact
            )[0];
            const primary = campaignDetail.team_members.filter(
                (x) => x.is_primary_contact
            )[0];
            setTeamMembers(campaignDetail.team_members);
            setPrimaryContact(primary);
            setEmergencyContact(emergency);
            setPermissionsLegend(
                campaign.campaignGlobals?.team_permissions_list
            );
            setLoading(false);
        }
    }, [campaign]);

    useEffect(() => {
        const access = person?.groups[0]?.name;
        if (access === 'limited team member') {
            setIsLimited(true);
        }
    }, [person]);

    useEffect(() => {
        if (!campaign) {
            getMyCampaign();
        }
    }, []);

    const getMyCampaign = async () => {
        try {
            const resp: MyCampaignResponse = await fetchMyCampaign();
            if (resp.detail && resp.detail === 'Invalid token.') {
                // Delete localStorage token
                localStorage.removeItem('token');
                history.push('/login');
            } else {
                campaignDispatch({
                    type: 'set_campaign',
                    payload: resp,
                });
            }
        } catch (e) {
            // Show some error here
        }
    };

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useReactTable({ columns, data });

    if (!campaign) {
        return <Loading />;
    }

    const { campaignCoordinator } = campaign;

    function showItems(member) {
        return member.permissions.map((item, i) => {
            return (
                <div key={i} className='me-2' style={{ fontSize: '22px' }}>
                    {renderIcon(item)}
                </div>
            );
        });
    }

    const ContactInfo = ({ member }: ITeamMemberItem) => {
        const mem = formatTeamMember(member);
        return (
            <div>
                <p style={{ fontWeight: '900' }}>{mem?.name}</p>
                <p>
                    <a href={`mailto:${mem?.email}`}>{mem?.email}</a>
                </p>
                <p>{mem?.phone}</p>
                <p>{mem?.relationship}</p>
                <p>{getGroupLabel(mem?.access)}</p>
            </div>
        );
    };

    const ContactInfoPatient = () => {
        const { person } = campaign.patient;
        const middleName = person.middle_name ? ` ${person.middle_name}` : '';
        return (
            <div>
                <p style={{ fontWeight: '900' }}>
                    {person.first_name}{middleName} {person.last_name}
                </p>
                <p>
                    <a href={`mailto:${person.email}`}>{person.email}</a>
                </p>
                <p>{person.phone}</p>
                <p>Client</p>
            </div>
        );
    };

    const PrimaryContactComponent = ({ member }: ITeamMemberItem) => {
        if (member) {
            return (
                <Col>
                    <Card
                        style={{
                            height: '100%',
                            borderWidth: '3px',
                            borderColor: '#01aeb4',
                        }}
                    >
                        <CardBody>
                            <div className='d-flex justify-content-between align-items-start'>
                                <p className='eyebrow'>Primary Contact</p>
                                {!isLimited && (
                                    <Button
                                        color='link'
                                        size='sm'
                                        className='py-0'
                                        onClick={() =>
                                            history.push(
                                                `/team/edit/${member.id}`,
                                                { member }
                                            )
                                        }
                                    >
                                        {renderIcon('edit')}
                                        Edit
                                    </Button>
                                )}
                            </div>
                            <ContactInfo member={member} />
                        </CardBody>
                    </Card>
                </Col>
            );
        }
        if (selfPrimary) {
            return (
                <Col>
                    <Card
                        style={{
                            height: '100%',
                            borderWidth: '3px',
                            borderColor: '#01aeb4',
                        }}
                    >
                        <CardBody>
                            <div className='d-flex justify-content-between align-items-start'>
                                <p className='eyebrow'>Primary Contact</p>
                                {!isLimited && (
                                    <Button
                                        color='link'
                                        size='sm'
                                        className='py-0'
                                        onClick={() =>
                                            history.push(`/patient/edit`)
                                        }
                                    >
                                        {renderIcon('edit')}
                                        Edit
                                    </Button>
                                )}
                            </div>
                            <ContactInfoPatient />
                        </CardBody>
                    </Card>
                </Col>
            );
        }
        return (
            <Col>
                <Card
                    style={{
                        height: '100%',
                        backgroundColor: 'rgba(1, 174, 180, 0.1)',
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <CardBody className='text-center'>
                        <Button
                            color='link'
                            size='sm'
                            className='mb-4'
                            onClick={() => setContactModal('primary')}
                        >
                            {renderIcon('add')}
                            Add Primary Contact
                        </Button>
                        <p>
                            The Primary Contact is the manager of this campaign.
                        </p>
                        <p>
                            We will reach out to the Primary Contact first when
                            we have questions or updates related to this
                            campaign.
                        </p>
                        <p>You can set yourself as the Primary Contact.</p>
                    </CardBody>
                </Card>
            </Col>
        );
    };

    const showPermissionsLegend = () => {
        if (campaign && permissions_legend != null) {
            return permissions_legend.map((item, i) => {
                return (
                    <div key={i}>
                        <span key={i} style={{ fontSize: '22px' }}>
                            {renderIcon(item.icon_name)}
                            {' = '}
                            {item.title}
                        </span>
                    </div>
                );
            });
        }
    };

    const EmergencyContactComponent = ({ member }: ITeamMemberItem) => {
        if (member) {
            return (
                <Col>
                    <Card
                        style={{
                            height: '100%',
                            borderWidth: '3px',
                            borderColor: '#952e46',
                        }}
                    >
                        <CardBody>
                            <div className='d-flex justify-content-between align-items-start'>
                                <p className='eyebrow'>Emergency Contact</p>
                                {!isLimited && (
                                    <Button
                                        color='link'
                                        size='sm'
                                        className='py-0'
                                        onClick={() =>
                                            history.push(
                                                `/team/edit/${member.id}`,
                                                { member }
                                            )
                                        }
                                    >
                                        {renderIcon('edit')}
                                        Edit
                                    </Button>
                                )}
                            </div>
                            <ContactInfo member={member} />
                        </CardBody>
                    </Card>
                </Col>
            );
        }
        return (
            <Col>
                <Card
                    style={{
                        height: '100%',
                        backgroundColor: 'rgba(149, 46, 70, 0.1)',
                        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.25)',
                    }}
                >
                    <CardBody className='text-center'>
                        <Button
                            color='link'
                            size='sm'
                            className='text-danger mb-4'
                            onClick={() => setContactModal('emergency')}
                        >
                            {renderIcon('add')}
                            Add Emergency Contact
                        </Button>
                        <p>
                            The Emergency Contact is authorized to make
                            decisions about this campaign on your behalf if you
                            are not able to respond.
                        </p>
                    </CardBody>
                </Card>
            </Col>
        );
    };

    const cancelDelete = () => {
        setDeleteModal(null);
    };

    const handleDelete = async () => {
        setDeleting(true);
        // Pass campaign ID if admin is logged in
        const adminCampaignId = isAdmin ? campaignId : undefined;
        const resp = await deleteTeamMember(deleteModal.id, adminCampaignId);
        // Update full campaign data including application items (for Contact card status)
        campaignDispatch({
            type: 'set_campaign',
            payload: resp,
        });
        setDeleting(false);
        setDeleteModal(null);
    };

    const handleContact = async (memberId, type) => {
        try {
            const payload = {
                member_id: memberId,
                type,
            };
            const campaignId = campaign?.campaignDetail?.campaign_id;
            const resp = await postCampaignContact(payload, campaignId);
            campaignDispatch({
                type: 'set_campaign',
                payload: resp,
            });
            setContactModal(null);
        } catch (e) {
            setContactModal(null);
        }
    };

    return (
        <div className='container__inner'>
            <CampaignHeaderSection
                leftcard={<AddTeamMemberCard />}
                contact={campaignCoordinator}
            />
            <Row className='row-cols-1 row-cols-lg-2 g-4 mb-5'>
                <PrimaryContactComponent member={primaryContact} />
                <EmergencyContactComponent member={emergencyContact} />
            </Row>
            <h2 className='h4'>Team Members</h2>
            <div className='pb-5'>
                <TableContainer
                    isServerSidePagination={false}
                    showHeaders
                    columns={columns}
                    data={data}
                    showPaginationComponents={false}
                    showFilterComponents={false}
                />
                <div className='my-4'>{showPermissionsLegend()}</div>
            </div>

            <ContactModal
                isOpen={contactModal}
                type={contactModal}
                toggle={() => setContactModal(null)}
                teamMembers={teamMembers}
                patient={campaign?.patient}
                handleContact={handleContact}
            />

            <Modal centered isOpen={!!deleteModal} toggle={cancelDelete}>
                <ModalBody>
                    <h4 className='text-modal modal__title'>
                        Are you sure you wish to remove
                        <br />
                        {deleteModal?.person.first_name}{' '}
                        {deleteModal?.person.last_name}?
                    </h4>
                    <Button
                        color='outline-dark'
                        onClick={cancelDelete}
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        color='danger'
                        onClick={handleDelete}
                        disabled={deleting}
                    >
                        {deleting ? `Saving...` : `Delete`}
                    </Button>
                </ModalBody>
            </Modal>
        </div>
    );
};
